import _ from 'lodash'
import { AppFormRelayResult, CodeNext, DraftExportSupplement, Export, FusionError, loadExportResult, loadExportV2Result, RenderMedia, serializeFusionError } from '@fusion-advantage/backcompat'
import { createDraftExportSupplement, exportToDisplayExport, linkedEncounterWriteMode, proposeContentLoadIgnoreLocation, proposeWorkItemStartEncounter, RootRouteFormContent, WorkItemAction, workItemWriteMode } from "@fusion-advantage/formlib"
import { selectIsPreviewMode, selectPreviewExport } from '../../redux/selectPreview'
import { AppThunk } from "../../redux/appStore"
import { AppFormRelayImpl, LocationStatus } from '@fusion-advantage/formrender'
import { updateRenderState } from "../relay/updateRenderState"
import { CloudToDeviceBlobType, CloudToDeviceBlobUpnAll, selectCloudToDeviceBlob } from '../../dal/ldbCloudToDeviceBlob'
import { selectDashboardDisplayIdentity } from '../../redux/dashboardDataSlice'
import { pushLogEntry, setLogLapEnd, setLogLapStart } from '../../redux/logSlice'
import { err, ok, Result } from 'neverthrow'
import { mustSelectExport } from '../../dal2/selectExport'
import { updateEncounterStatus } from '../../dal2/updateEncounterStatus'
import { AppModal, pushAppModal, setLdng } from '../../redux/appSlice'
import { selectLocationStatus, setLocationNextModalSkipContent } from '../../redux/locationSlice'

interface appFormLoadPhase1Ok {
	exp: Export
	draftExportSupplement?: DraftExportSupplement
}

export const appFormLoadPhase1 = (content: RootRouteFormContent): AppThunk<Promise<Result<appFormLoadPhase1Ok, FusionError>>> => async (dispatch, getState) => {
	const {
		renderInstanceId,
		encounterGuid,
		action,
		workItem,
	} = content
	// dispatch(openAppModal(AppModal.Log))
	dispatch(setLogLapStart())
	dispatch(pushLogEntry(`[appFormLoad] render instance ${renderInstanceId} begin phase 1 for encounter ${encounterGuid}`))
	dispatch(updateRenderState({
		formLoading: true,
		authedIdentity: getState().dashboardData.authedIdentity,
		impersonatingIdentity: getState().dashboardData.impersonatingIdentity,
	}))
	dispatch(setLdng({form: true}))
	const rLoad = await (async (): Promise<Result<appFormLoadPhase1Ok, FusionError>> => {
		// if form preview mode, use hardcoded preview export
		// (and never fall through to loading a real encounter)
		if(selectIsPreviewMode(getState())) {
			const previewExp = selectPreviewExport(getState())
			if(!previewExp) {
				return err(serializeFusionError('appFormLoadPhase1', 'preview branch designator not yet known'))
			}
			return ok({
				exp: previewExp,
				draftExportSupplement: createDraftExportSupplement(),
			})
		}
		if(!proposeContentLoadIgnoreLocation(content, getState().app.environment).isOk()) {
			switch(selectLocationStatus(getState())) {
				case LocationStatus.Success:
				case LocationStatus.Refreshing:
					break
				default:
					dispatch(setLocationNextModalSkipContent(content))
					dispatch(pushAppModal(AppModal.Location))
					return err({
						name: 'Error',
						area: 'appFormLoad',
						// TODO we have better messaging in the new dialog,
						// so this message doesn't need to pop up anymore
						// message: 'Your location could not be queried because location services are not enabled for this app.  Please enable, or contact support for assistance.',
						message: '',
					})
			}
		}
		const user = selectDashboardDisplayIdentity(getState())
		if(!user) {
			return err(serializeFusionError('appFormLoad', 'no user identity available'))
		}
		let rExport = await mustSelectExport(user.upn, encounterGuid)
		if(rExport.isErr()) {
			return err(rExport.error)
		}
		let exp = rExport.value.exp
		let dexp = exportToDisplayExport(exp)
		const prevCodeNext = exp.Form.Encounter.CodeNext
		switch(action) {
			case WorkItemAction.StartEncounter:
				if(!workItem) {
					return err(serializeFusionError('appFormLoad', 'work item must be provided for encounter start'))
				}
				const rStart = proposeWorkItemStartEncounter(workItem, dexp, getState().dashboardData.filterByDate)
				if(rStart.isErr()) {
					return err(serializeFusionError('appFormLoad', 'cannot start encounter: ' + rStart.error))
				}
				const { nextCodeNext } = rStart.value
				dispatch(pushLogEntry(`[appFormLoad] writing encounter status update '${prevCodeNext}' -> '${nextCodeNext}'`))
				const rStatus = await dispatch(updateEncounterStatus(encounterGuid, nextCodeNext))
				if(rStatus.isErr()) {
					return err(serializeFusionError('appFormLoad', rStatus.error))
				}
				// reload export after writing status update
				rExport = await mustSelectExport(user.upn, encounterGuid)
				if(rExport.isErr()) {
					return err(rExport.error)
				}
				exp = rExport.value.exp
				break
			case WorkItemAction.ViewFormAsAuditor:
				// patch the encounter with alternate temporary code
				// only required for the session; do not persist to local db
				exp.Form.Encounter.CodeNext = CodeNext.Level1Audit
				break
		}
		if(exp.Form.Encounter.CodeNext !== prevCodeNext) {
			dispatch(pushLogEntry(`[appFormLoad] export CodeNext patched '${prevCodeNext}' -> '${exp.Form.Encounter.CodeNext}'`))
		}
		return ok({
			exp,
			draftExportSupplement: createDraftExportSupplement(),
		})
	})()
	if(rLoad.isErr()) {
		dispatch(updateRenderState({formLoading: false}))
		dispatch(setLdng({form: false}))
	}
	dispatch(setLogLapEnd())
	dispatch(pushLogEntry(`[appFormLoad] end phase 1`))
	return rLoad
}

interface appFormLoadPhase2Args {
	appFormRelay: AppFormRelayImpl
	action: WorkItemAction
	exp: Export
	draftExportSupplement?: DraftExportSupplement
	// optional: restrict view to only one page of the form
	onlyPageIdx?: number
	openedFromLink?: boolean
}

export const appFormLoadPhase2 = ({
	appFormRelay,
	action,
	exp,
	draftExportSupplement,
	onlyPageIdx,
	openedFromLink,
}: appFormLoadPhase2Args): AppThunk<Promise<Result<void, FusionError>>> => async (dispatch, getState) => {
	dispatch(pushLogEntry('[appFormLoad] begin phase 2'))
	const rLoad2 = await (async (): Promise<Result<void, FusionError>> => {
		let loadResult: AppFormRelayResult<loadExportResult|loadExportV2Result> = {
			detached: false,
		}
		const branchGuid = exp.Form.Encounter.BranchGUID
		const rFlow = await selectCloudToDeviceBlob({
			type: CloudToDeviceBlobType.Flowmap,
			id: branchGuid,
			upn: CloudToDeviceBlobUpnAll,
		})
		if(rFlow.isErr()) {
			return err(serializeFusionError('appFormLoad', rFlow.error))
		}
		if(!rFlow.value) {
			return err(serializeFusionError('appFormLoad', `null flowmap for branch ${branchGuid}`))
		}
		const decoder = new TextDecoder()
		const flowXml = decoder.decode(rFlow.value.data)
		let writeMode = workItemWriteMode(action, exp)
		if(openedFromLink) {
			writeMode = linkedEncounterWriteMode(writeMode, exp, selectDashboardDisplayIdentity(getState()))
		}
		dispatch(updateRenderState({
			form: {
				flowXml,
				export: exp,
				writeMode,
				onlyPageIdx,
			},
		}))
		dispatch(pushLogEntry(`[appFormLoad] write mode '${writeMode}'`))
		try {
			if(appFormRelay.loadExportV2) {
				loadResult = await appFormRelay.loadExportV2({
					draftExportSupplement,
				})
			} else {
				loadResult = appFormRelay.loadExport({
					draftExportSupplement,
				})
			}
		} catch(ex) {
			return err(serializeFusionError('formrender loadExport', ex))
		}
		if(loadResult.detached) {
			return err(serializeFusionError('appFormLoad', 'form has been detached'))
		} else if(!loadResult.result?.success) {
			// TODO
			// alert message already displayed by loadExport
			// ...let's get away from this pattern though
			return err(serializeFusionError('formrender loadExport', 'loadExport did not return success'))
		}
		return ok()
	})()
	let media = RenderMedia.Screen
	if(action === WorkItemAction.ViewFormAsDocgen) {
		media = RenderMedia.Print
	}
	dispatch(updateRenderState({
		formLoading: false,
		media,
		previewMode: selectIsPreviewMode(getState()),
	}))
	dispatch(setLdng({form: false}))
	dispatch(pushLogEntry(`[appFormLoad] end phase 2`))
	return rLoad2
}
